<template>
  <div class="mb-7">
    <Introduction />

    <slot
      :current-step="currentStep"
      name="sticky-bar"
      :title="selectedTitle"
      :total-steps="funnel.length"
    />

    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
    <div
      v-for="(step, index) in funnel"
      :key="step.id"
      class="py-56 md:py-24"
      @focus="() => handleFocusedStep(index)"
      @mouseenter="() => handleFocusedStep(index)"
      @mousemove="() => handleFocusedStep(index)"
    >
      <Step
        ref="steps"
        :is-focused="isFocusedStep(index)"
        :product="product"
        :step="step"
      >
        <template #description-grade>
          <ConditionDescription />
        </template>

        <template #guidance-grade>
          <ConditionGuidance :tracking-model="product.model" />
        </template>

        <template #guidance-color>
          <Gallery
            :allow-marketing-images="false"
            :allow-media-viewer="false"
            class="mb-16"
            :images="images"
          />
        </template>
      </Step>
    </div>

    <Proposal
      ref="proposal"
      :grade="grade"
      :is-focused="isProposalFocused"
      :product="product"
      :selected-color-code="selectedColorCode"
      :selected-title="selectedTitle"
      :swap-offer="swapOffer"
      :swap-status="swapStatus"
      :tracking="productTracking"
      @focus="() => handleFocusedProposal()"
      @mouseenter="() => handleFocusedProposal()"
    >
      <template #favorites>
        <slot name="favorites" />
      </template>
      <template #details>
        <slot name="details" />
      </template>
    </Proposal>
  </div>

  <NewBatteryModal :device-name="product.rawTitle" />
  <SimModal :show-trigger="false" />
  <TradeInModal />
</template>

<script lang="ts" setup>
import { useRoute } from '#imports'
import { computed, onMounted, onUnmounted, ref } from 'vue'

import { type InternalLink, productAPI } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { urlHashToObject } from '@backmarket/utils/url/urlHashToObject'
import { useIntersectionObserver } from '@vueuse/core'

import { type Estimation } from '~/scopes/buyback/api/adapters/getEstimationSwapPrice.adapter'
import { SWAP_NONE } from '~/scopes/buyback/swap/components/SwapBlock/constants'

import { useProductTracking } from '../../composables/useProductTracking'
import Gallery from '../Gallery/Gallery.vue'
import NewBatteryModal from '../Pickers/components/NewBattery/components/Modal/Modal.vue'
import SimModal from '../Pickers/components/SimModal/SimModal.vue'

import ConditionDescription from './components/ConditionDescription/ConditionDescription.vue'
import ConditionGuidance from './components/ConditionGuidance/ConditionGuidance.vue'
import Introduction from './components/Introduction/Introduction.vue'
import Proposal from './components/Proposal/Proposal.vue'
import Step from './components/Step/Step.vue'
import TradeInModal from './components/TradeInModal/TradeInModal.vue'
import { createCustomizationFunnelData } from './utils/createCustomizationFunnelData'

const props = withDefaults(
  defineProps<{
    grade: productAPI.GetBestOffersResponse[number]
    pickers: productAPI.GetPickersResponse['pickers']
    product: productAPI.GetProductResponse
    productTracking: ReturnType<typeof useProductTracking>
    swapOffer?: Estimation
    swapStatus?: string
  }>(),
  {
    swapOffer: undefined,
    swapStatus: SWAP_NONE,
  },
)

const route = useRoute()
const i18n = useI18n()
const proposal = ref<HTMLDivElement | null>(null)
const proposalObserver = ref<ReturnType<typeof useIntersectionObserver>>()
const lastYPosition = ref(0)
const currentStep = ref(0)
const steps = ref<HTMLDivElement[] | null>([])
const stepsObservers = ref<ReturnType<typeof useIntersectionObserver>[]>([])

const funnel = computed(() => {
  return createCustomizationFunnelData(
    props.pickers,
    props.product,
    {
      ...route,
      hash: urlHashToObject(route.hash),
      type: 'internal',
    } as InternalLink,
    props.swapStatus,
    props.swapOffer,
  )
})

const selectedOptions = computed(() => {
  return funnel.value
    .reduce<Array<string>>((acc, { options, type }) => {
      if (type !== 'service') {
        const label =
          options?.find(({ selected }) => !!selected)?.label ?? undefined
        if (label) {
          acc.push(typeof label === 'object' ? i18n(label) : label)
        }
      }

      return acc
    }, [])
    .filter(Boolean)
})

const selectedTitle = computed(() => {
  return selectedOptions.value.join(' - ')
})

const selectedColorCode = computed(() => {
  return (
    funnel.value
      ?.find(({ id }) => id === 'color')
      ?.options?.find(({ selected }) => !!selected)?.color ?? undefined
  )
})

const images = computed(() => {
  return props.product.images.slice(0, 3)
})

function isStepObserverInitialized(index: number) {
  return (
    steps.value &&
    steps.value.length > 0 &&
    stepsObservers &&
    stepsObservers.value.length > 0 &&
    stepsObservers.value[index].isActive
  )
}

function handleFocusedStep(index: number) {
  if (isStepObserverInitialized(index)) {
    currentStep.value = index
  }
}

const isProposalObserverInitialized = computed(() => {
  return proposal.value && proposalObserver.value?.isActive
})

function handleFocusedProposal() {
  if (isProposalObserverInitialized.value) {
    currentStep.value = funnel.value.length
  }
}

onMounted(() => {
  funnel.value.forEach((_step, index) => {
    // When more than 3/4 of a step is displaying, it became the focus state.
    // We remove the focus the previous step when it starts to disappear.

    // TO Fix
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: Unreachable code error
    stepsObservers.value[index] = useIntersectionObserver(
      steps.value?.[index],
      ([{ isIntersecting, boundingClientRect, intersectionRatio }]) => {
        if (
          isIntersecting &&
          parseFloat((Math.round(intersectionRatio * 4) / 4).toFixed(2)) >= 0.5
        ) {
          // On scroll down, we give the priority to the next step coming from the bottom.
          // On scroll up, we give the priority to the previous step coming from the top.
          if (
            (lastYPosition.value > window.scrollY &&
              boundingClientRect.top < 0) ||
            (lastYPosition.value <= window.scrollY &&
              boundingClientRect.top > 0)
          ) {
            handleFocusedStep(index)
          }
        }
        lastYPosition.value = window.scrollY
      },
      {
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    )
  })

  proposalObserver.value = useIntersectionObserver(
    proposal,
    ([{ boundingClientRect, isIntersecting, intersectionRatio }]) => {
      // When more than 3/4 of a step is displaying, it became the focus state.
      // We remove the focus the previous step when it starts to disappear.
      if (
        isIntersecting &&
        parseFloat((Math.round(intersectionRatio * 4) / 4).toFixed(2)) >= 0.25
      ) {
        if (
          (lastYPosition.value > window.scrollY &&
            boundingClientRect.top < 0) ||
          (lastYPosition.value <= window.scrollY && boundingClientRect.top > 0)
        ) {
          handleFocusedProposal()
        }
      }
      lastYPosition.value = window.scrollY
    },
    {
      threshold: [0, 0.25, 0.5, 0.75, 1],
    },
  )
})

const isProposalFocused = computed(() => {
  return isProposalObserverInitialized.value
    ? currentStep.value === funnel.value.length
    : true
})

function isFocusedStep(index: number) {
  return isStepObserverInitialized(index) ? currentStep.value === index : true
}
onUnmounted(() => {
  stepsObservers.value.forEach((observer) => {
    observer.stop()
  })
  proposalObserver.value?.stop()
})
</script>
